import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { PackageCheckIcon, PlusIcon, RotateCcwIcon, Trash2Icon } from 'lucide-react';
type DrugForm = {
  medicineId: string;
  batchNumber: string;
  quantity: string;
  buyingPrice: string;
  sellingPrice: string;
  manufactureDate: string;
  expiryDate: string;
};
type StagedDrug = DrugForm & {
  id: string;
  medicineName: string;
  unit: string;
};
const emptyDrug: DrugForm = {
  medicineId: '',
  batchNumber: '',
  quantity: '',
  buyingPrice: '',
  sellingPrice: '',
  manufactureDate: '',
  expiryDate: ''
};
export function ReceiveStock() {
  const {
    medicines,
    suppliers,
    purchaseOrders,
    receiveStock,
    currentUser
  } = useData();
  const manager = currentUser?.role === 'Pharmacy Manager';
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [purchaseOrderId, setPurchaseOrderId] = useState('');
  const [notes, setNotes] = useState('');
  const [drug, setDrug] = useState<DrugForm>(emptyDrug);
  const [stagedDrugs, setStagedDrugs] = useState<StagedDrug[]>([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const activeOrders = useMemo(() => purchaseOrders.filter(order => order.kind !== 'Receipt' && ['Pending', 'Partially Received'].includes(order.status)), [purchaseOrders]);
  const selectedOrder = activeOrders.find(order => order.id === purchaseOrderId);
  const availableMedicines = selectedOrder ? medicines.filter(medicine => selectedOrder.lines.some(line => line.medicineId === medicine.id && line.quantityReceived < line.quantityOrdered)) : medicines.filter(medicine => medicine.status === 'Active');
  const addDrug = () => {
    setError('');
    setMessage('');
    const medicine = medicines.find(item => item.id === drug.medicineId);
    if (!medicine) return setError('Select a medicine before adding it to the receipt.');
    if (!Number.isFinite(Number(drug.quantity)) || Number(drug.quantity) <= 0) return setError('Enter a quantity greater than zero.');
    if (!Number.isFinite(Number(drug.buyingPrice)) || Number(drug.buyingPrice) < 0 || !Number.isFinite(Number(drug.sellingPrice)) || Number(drug.sellingPrice) < 0) return setError('Enter valid buying and selling prices.');
    if (!drug.manufactureDate || !drug.expiryDate || new Date(drug.expiryDate) <= new Date(drug.manufactureDate)) return setError('Enter manufacture and expiry dates with expiry after manufacture.');
    setStagedDrugs(current => [...current, {
      ...drug,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      medicineName: medicine.name,
      unit: medicine.unit
    }]);
    setDrug(emptyDrug);
  };
  const clearForm = () => {
    setSupplierId('');
    setInvoiceNumber('');
    setPurchaseOrderId('');
    setNotes('');
    setDrug(emptyDrug);
    setStagedDrugs([]);
    setError('');
    setMessage('');
  };
  const updateInventory = async () => {
    setError('');
    setMessage('');
    if (!supplierId || !invoiceNumber.trim() || !stagedDrugs.length) {
      setError('Supplier, invoice number, and at least one staged drug are required.');
      return;
    }
    setSaving(true);
    const result = await receiveStock({
      supplierId,
      invoiceNumber: invoiceNumber.trim(),
      purchaseOrderId: purchaseOrderId || undefined,
      notes,
      items: stagedDrugs.map(({
        medicineId,
        batchNumber,
        quantity,
        buyingPrice,
        sellingPrice,
        manufactureDate,
        expiryDate
      }) => ({
        medicineId,
        batchNumber: batchNumber.trim() || undefined,
        quantity: Number(quantity),
        buyingPrice: Number(buyingPrice),
        sellingPrice: Number(sellingPrice),
        manufactureDate,
        expiryDate
      }))
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error || 'Inventory could not be updated.');
      return;
    }
    clearForm();
    setMessage('Inventory Updated Successfully.');
  };
  return <div className="max-w-6xl space-y-5" data-id="element-5029">
      <div data-id="element-5030">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-5031">Receive New Stock</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-5032">
          Stage a complete delivery first. Inventory is changed only once, after
          the entire receipt passes validation.
        </p>
      </div>
      {message && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800" data-id="element-5033">
          {message}
        </p>}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800" data-id="element-5034">
          {error}
        </p>}

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-5035">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-5036">
          <Select label="Supplier" required value={supplierId} onChange={event => setSupplierId(event.target.value)} options={[{
          value: '',
          label: 'Select supplier'
        }, ...suppliers.filter(supplier => supplier.status === 'Active').map(supplier => ({
          value: supplier.id,
          label: supplier.name
        }))]} data-id="element-5037" />
          <Input label="Invoice number" required value={invoiceNumber} onChange={event => setInvoiceNumber(event.target.value)} data-id="element-5038" />
          <Select label="Purchase order (optional)" value={purchaseOrderId} onChange={event => {
          const order = activeOrders.find(item => item.id === event.target.value);
          setPurchaseOrderId(event.target.value);
          setSupplierId(order?.supplierId || supplierId);
          setDrug(emptyDrug);
        }} options={[{
          value: '',
          label: 'Independent receipt'
        }, ...activeOrders.map(order => ({
          value: order.id,
          label: `${order.orderNumber} · ${order.supplierName}`
        }))]} data-id="element-5039" />
        </div>
        <label className="mt-4 block text-sm font-medium text-slate-700" data-id="element-5040">
          Receiving remarks{' '}
          <textarea value={notes} onChange={event => setNotes(event.target.value)} rows={2} className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600" placeholder="Delivery inspection or receipt remarks" data-id="element-5041" />
        </label>
      </section>

      <section className="rounded-xl border border-teal-200 bg-teal-50/40 p-5" data-id="element-5042">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2" data-id="element-5043">
          <div data-id="element-5044">
            <h3 className="font-bold text-slate-900" data-id="element-5045">Add drug to receipt</h3>
            <p className="mt-1 text-xs text-slate-600" data-id="element-5046">
              Leave batch number blank to generate it centrally when inventory
              is updated.
            </p>
          </div>
          <span className="text-xs font-semibold text-teal-800" data-id="element-5047">
            Received by: {currentUser?.fullName || 'Signed-in user'}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-5048">
          <Select label="Medicine" required value={drug.medicineId} onChange={event => {
          const selectedMedicine = medicines.find(medicine => medicine.id === event.target.value);
          setDrug({
            ...drug,
            medicineId: event.target.value,
            buyingPrice: selectedMedicine ? String(selectedMedicine.buyingPrice) : '',
            sellingPrice: selectedMedicine ? String(selectedMedicine.sellingPrice) : ''
          });
        }} options={[{
          value: '',
          label: 'Select medicine'
        }, ...availableMedicines.map(medicine => ({
          value: medicine.id,
          label: `${medicine.name} · ${medicine.drugCode}`
        }))]} data-id="element-5049" />
          <Input label="Batch number (optional)" value={drug.batchNumber} onChange={event => setDrug({
          ...drug,
          batchNumber: event.target.value
        })} data-id="element-5050" />
          <Input label="Quantity received" required type="number" min="1" value={drug.quantity} onChange={event => setDrug({
          ...drug,
          quantity: event.target.value
        })} data-id="element-5051" />
          <Input label="Buying price (ZMW)" required type="number" min="0" step="0.01" value={drug.buyingPrice} disabled={!manager} onChange={event => setDrug({
          ...drug,
          buyingPrice: event.target.value
        })} data-id="element-5052" />
          <Input label="Selling price (ZMW)" required type="number" min="0" step="0.01" value={drug.sellingPrice} onChange={event => setDrug({
          ...drug,
          sellingPrice: event.target.value
        })} data-id="element-5053" />
          <Input label="Manufacture date" required type="date" max={new Date().toISOString().slice(0, 10)} value={drug.manufactureDate} onChange={event => setDrug({
          ...drug,
          manufactureDate: event.target.value
        })} data-id="element-5054" />
          <Input label="Expiry date" required type="date" min={drug.manufactureDate || undefined} value={drug.expiryDate} onChange={event => setDrug({
          ...drug,
          expiryDate: event.target.value
        })} data-id="element-5055" />
          <div className="flex items-end" data-id="element-5056">
            <Button type="button" onClick={addDrug} className="w-full bg-teal-600 hover:bg-teal-700" data-id="element-5057">
              <PlusIcon className="mr-2 h-4 w-4" data-id="element-5058" /> Add Drug
            </Button>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5059">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4" data-id="element-5060">
          <div data-id="element-5061">
            <h3 className="font-bold text-slate-900" data-id="element-5062">Temporary order list</h3>
            <p className="mt-1 text-sm text-slate-500" data-id="element-5063">
              {stagedDrugs.length} line{stagedDrugs.length === 1 ? '' : 's'}{' '}
              staged · total{' '}
              {new Intl.NumberFormat('en-ZM', {
              style: 'currency',
              currency: 'ZMW'
            }).format(stagedDrugs.reduce((sum, item) => sum + Number(item.quantity) * Number(item.buyingPrice), 0))}
            </p>
          </div>
          <Button variant="outline" onClick={clearForm} data-id="element-5064">
            <RotateCcwIcon className="mr-2 h-4 w-4" data-id="element-5065" /> Clear Form
          </Button>
        </div>
        <div className="overflow-x-auto" data-id="element-5066">
          <table className="min-w-[900px] w-full divide-y divide-slate-100" data-id="element-5067">
            <thead className="bg-slate-50" data-id="element-5068">
              <tr data-id="element-5069">
                {['Medicine', 'Batch', 'Quantity', 'Buying price', 'Selling price', 'Dates', ''].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5070">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5071">
              {stagedDrugs.map(item => <tr key={item.id} data-id="element-5072">
                  <td className="px-5 py-3 text-sm font-semibold text-slate-900" data-id="element-5073">
                    {item.medicineName}
                    <span className="ml-2 text-xs font-normal text-slate-500" data-id="element-5074">
                      {item.unit}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-600" data-id="element-5075">
                    {item.batchNumber || 'Generated on update'}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-700" data-id="element-5076">
                    {item.quantity}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-700" data-id="element-5077">
                    ZMW {item.buyingPrice}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-700" data-id="element-5078">
                    ZMW {item.sellingPrice}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500" data-id="element-5079">
                    Mfg {item.manufactureDate}
                    <br data-id="element-5080" />
                    Exp {item.expiryDate}
                  </td>
                  <td className="px-5 py-3 text-right" data-id="element-5081">
                    <button type="button" onClick={() => setStagedDrugs(current => current.filter(staged => staged.id !== item.id))} className="rounded p-2 text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500" aria-label={`Remove ${item.medicineName}`} data-id="element-5082">
                      <Trash2Icon className="h-4 w-4" data-id="element-5083" />
                    </button>
                  </td>
                </tr>)}
              {!stagedDrugs.length && <tr data-id="element-5084">
                  <td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-500" data-id="element-5085">
                    No medicines have been staged. Add a drug to begin this
                    receipt.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end border-t border-slate-100 px-5 py-4" data-id="element-5086">
          <Button onClick={updateInventory} disabled={saving || !stagedDrugs.length} className="bg-teal-600 hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-5087">
            <PackageCheckIcon className="mr-2 h-4 w-4" data-id="element-5088" />
            {saving ? 'Updating inventory…' : 'Update Inventory'}
          </Button>
        </div>
      </section>
    </div>;
}